/* eslint-disable @typescript-eslint/no-explicit-any */

// ***** Import from packages *****
import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";

// ***** Import from files *****
import { toast } from "react-toastify";
import apiContextHelper from "@/composables/apiContextHelper";
import { endpoints } from "@/constants/api";
import abortControllerHandler from "@/api/abortControllerHandler";

// ***** Type definitions *****
type ApiMethod = "get" | "post" | "put" | "patch" | "delete";

interface ApiResponse<T = unknown> {
  status: number;
  data?: T;
  message?: string;
  ok?: boolean;
}

interface ApiError {
  status: number;
  message: string;
  data?: unknown;
}

interface AxiosErrorCustom extends AxiosError {
  response?: AxiosResponse<unknown> & {
    data?: {
      message?: string;
    };
  };
}

interface ApiOptions extends AxiosRequestConfig {
  headers?: Record<string, string>;
  responseType?: "json" | "stream";
}

const {
  createHeaders,
  handleLogout,
  API_CONFIG,
  getUnStringified,
  getToken,
  refreshToken,
  RefreshTokenStatusCode,
  isRefreshTokenRequest,
} = apiContextHelper();

// ***** Utility functions *****

/**
 * Makes an HTTP request to the specified endpoint
 * @param endpoint - API endpoint
 * @param data - Request payload
 * @param type - HTTP method
 * @param isNonAuthenticated - Whether to skip authentication
 * @param customHeader - Additional headers
 * @param isManualEndpoint - Whether to use endpoint as complete URL
 * @param customOptions - Additional axios options
 * @param isStream - Whether response is a stream
 */
export const api = async <T = unknown>(
  endpoint: string,
  data: unknown = {},
  type: ApiMethod,
  isNonAuthenticated = false,
  customHeader: Record<string, string> = {},
  isManualEndpoint = false,
  customOptions: ApiOptions = {},
  isStream = false,
): Promise<ApiResponse<T | Blob>> => {
  try {
    const headers = createHeaders(isNonAuthenticated, customHeader);
    const url = isManualEndpoint
      ? endpoint
      : `${API_CONFIG.baseURL || ""}${endpoint}`;

    const config: AxiosRequestConfig = {
      ...customOptions,
      headers,
      responseType: isStream ? "blob" : "json",
    };

    let response: AxiosResponse<T>;

    switch (type) {
      case "get":
        response = await axios.get<T>(url, config);
        break;
      case "delete":
        response = await axios.delete<T>(url, { ...config, data });
        break;
      case "post":
      case "put":
      case "patch":
        response = await axios[type]<T>(url, data, config);
        break;
      default:
        throw new Error(`Invalid request type: ${type}`);
    }

    return {
      status: response.status,
      data: isStream ? (response.data as Blob) : response.data,
      ok: true,
    };
  } catch (error) {
    const axiosError = error as AxiosErrorCustom;
    const apiError: ApiError = {
      status: axiosError.response?.status ?? 500,
      message:
        axiosError.response?.data?.message ??
        axiosError.message ??
        "Unknown error occurred",
      data: axiosError.response?.data,
    };

    return {
      status: apiError.status,
      message: apiError.message,
      data: apiError.data as T,
      ok: false,
    };
  }
};

// Optional: Add request/response interceptors
axios.interceptors.request.use(
  (config) => {
    // Add any request transformations
    const { getAbortController } = abortControllerHandler();
    config.signal = getAbortController().signal;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.config.signal.aborted) {
      return Promise.reject(error);
    }
    // Handle specific error cases
    if (error.response?.status === 401) {
      // Handle unauthorized access
      handleLogout();
    } else if (error.response?.status === 403) {
      toast.error("You are not authorized to access this resource");
    } else if (error.response?.status === RefreshTokenStatusCode) {
      error.message = null;
      if (!isRefreshTokenRequest(error)) {
        refreshToken();
      } else {
        handleLogout();
      }
    }
    return Promise.reject(error);
  },
);
